import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { createReferenceStringWithHistory } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { Separator } from '@seen/ui'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import {
  buildCompositionActivity,
  type CompositionActivityNoun,
} from '#src/components/activity/BuildCompositionActivity.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { CommentComposer } from '#src/components/CommentComposer.tsx'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { groupCompositionLabelActivityEntries } from '#src/utils/GroupLabelActivityEntries.tsx'

export interface CompositionActivityProps {
  composition: ExtendedComposition
  /** Noun used in the "created the …" label. */
  resourceNoun: CompositionActivityNoun
}

/*
 * Activity feed + comment composer for a `Composition`. Renders the deduped
 * created/signed/edited history interleaved chronologically with
 * `CommentCommunication`s targeting the composition, plus a composer for new
 * comments. Shared between the assessment and visit-note pages.
 */
export function CompositionActivity({
  composition,
  resourceNoun,
}: CompositionActivityProps): JSX.Element {
  const { data: versions = [] } = useResourceVersions(
    ExtendedComposition,
    composition.id
  )
  // `versions` is referentially stable between renders until the query refetches,
  // so keying off it avoids re-running the deep-equal section diff on unrelated
  // re-renders (this feed sits beside the visit-note collaborative editor).
  const events = useMemo(
    () =>
      groupCompositionLabelActivityEntries(
        buildCompositionActivity(versions, resourceNoun)
      ),
    [versions, resourceNoun]
  )
  const compositionOriginOf = useVersionOriginTask(versions)

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([composition.reference])
  )

  const activity = useMemo(
    () =>
      orderBy(
        [...events, ...comments],
        (item) =>
          item instanceof CommentCommunication
            ? item.sent
            : item.composition.lastUpdated,
        'desc'
      ).map((item) => {
        if (item instanceof CommentCommunication) {
          return (
            <ActivityComment key={item.reference.reference} comment={item} />
          )
        }
        return (
          <ActivityEntry
            key={
              item.key ??
              createReferenceStringWithHistory(
                item.composition.toFhirResource()
              )
            }
            author={item.composition.lastUpdatedBy}
            time={item.composition.lastUpdated}
            action={item.action}
            originTask={compositionOriginOf(item.composition)}
          />
        )
      }),
    [events, comments, compositionOriginOf]
  )

  const subject = isOfType(composition.subject, 'Patient')
    ? composition.subject
    : undefined

  return (
    <div className="flex flex-col gap-4">
      <Separator />
      <h5>Activity</h5>
      <div className="-mx-1.5">
        <CommentComposer partOf={[composition.reference]} subject={subject} />
      </div>
      <ActivityList>{activity}</ActivityList>
    </div>
  )
}
